import config from "../../config";
import { Region } from "../../graphql";
import { auth } from "../../keycloak";
import { reportSilently } from "../../util/reportError";

/**
 * Resolves the REST/Connect (Vanguard) API base URL for the given NGP region.
 * EU → EUR API, US → INT API. DEV (and anything unmapped) falls back to EUR.
 * Defaults are production; both URLs are configurable via config.api.endpoints.
 */
export const regionToApiBase = (region: Region | undefined): string => {
  const endpoints = config.api.endpoints;
  switch (region) {
    case Region.Us:
      return endpoints.US;
    case Region.Eu:
    case Region.Dev:
    default:
      return endpoints.EU;
  }
};

/**
 * Fetches a signed JWT for Intercom Fin authentication from the support API.
 *
 * The website calls the `GetFinToken` RPC over gRPC-web; NGP has no gRPC
 * connection to our APIs, so we hit the Vanguard REST proxy that transcodes the
 * same RPC: GET {apiBase}/v1/support/fin-token, authenticated with the
 * Keycloak access token. The empty RPC request maps to a no-body GET.
 *
 * Returns the token string, or null on any failure (caller fails silently,
 * matching the website's behaviour of not booting Intercom on error).
 */
export const fetchFinToken = async (region: Region | undefined): Promise<string | null> => {
  const accessToken = auth.accessToken;
  if (!accessToken) {
    return null;
  }

  try {
    const response = await fetch(`${regionToApiBase(region)}/v1/support/fin-token`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      // Bound the request so a hung Vanguard response can't leave Intercom
      // forever-pending. The AbortError is caught below and reported.
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      reportSilently(new Error(`Fin token request failed: HTTP ${response.status}`), {
        feature: "intercom",
        op: "fetchFinToken",
      });
      return null;
    }

    const body = (await response.json()) as { token?: string };
    return body.token ?? null;
  } catch (err) {
    reportSilently(err, { feature: "intercom", op: "fetchFinToken" });
    return null;
  }
};
